import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import Breadcrumb from "../components/Breadcrumb/Breadcrumb";

/* Ant Design */
import {
  Alert,
  Button,
  Card,
  Divider,
  Flex,
  Grid,
  Input,
  Modal,
  Row,
  Col,
  Skeleton,
  Space,
  Tooltip,
  Typography,
  message,
  notification,
} from "antd";
import {
  InfoCircleOutlined,
  SaveOutlined,
  UndoOutlined,
  LoginOutlined,
  UserOutlined,
  MailOutlined,
  LockOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const ProfileSettingsPage = () => {
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const { authData } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [error, setError] = useState("");

  // profile fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName]   = useState("");
  const [email, setEmail]         = useState("");

  // server-required fields for PUT
  const [isActive, setIsActive]   = useState(true);
  const [roleId, setRoleId]       = useState(""); // current role id to send back

  // password fields
  const [oldPassword, setOldPassword]                 = useState("");
  const [newPassword, setNewPassword]                 = useState("");
  const [confirmNewPassword, setConfirmNewPassword]   = useState("");

  const changingPassword = useMemo(
    () => !!(oldPassword || newPassword || confirmNewPassword),
    [oldPassword, newPassword, confirmNewPassword]
  );

  /* Load profile + role id */
  useEffect(() => {
    let mounted = true;
    (async () => {
      setError("");
      try {
        // 1) basic profile
        const prof = await axiosInstance.get("/user/profile");
        if (!mounted) return;
        setFirstName(prof?.data?.firstName || "");
        setLastName(prof?.data?.lastName || "");
        setEmail(prof?.data?.email || "");
        setIsActive(!!prof?.data?.isActive);

        // 2) current role id (needed by PUT endpoint)
        const userId = authData?.userId;
        if (userId) {
          const rolesRes = await axiosInstance.get(`/user/${userId}/Roles`);
          const roles = rolesRes?.data?.roles || [];
          // pick first role (or keep empty if none)
          setRoleId(roles[0]?.id || "");
        }
      } catch (e) {
        if (!mounted) return;
        setError("Error fetching profile data.");
        console.error(e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [authData?.userId]);

  const validate = () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      message.warning("First name, last name, and email are required.");
      return false;
    }
    if (changingPassword) {
      if (!oldPassword)      { message.error("Enter your current password to change password."); return false; }
      if (!newPassword || !confirmNewPassword) {
        message.error("Fill both new password fields."); return false;
      }
      if (newPassword !== confirmNewPassword) {
        message.error("New password and confirmation do not match."); return false;
      }
      if (newPassword.length < 8) {
        message.error("New password must be at least 8 characters."); return false;
      }
    }
    return true;
  };

  const onResetPasswords = () => {
    setOldPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
  };

  const onReload = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get("/user/profile");
      setFirstName(res?.data?.firstName || "");
      setLastName(res?.data?.lastName || "");
      setEmail(res?.data?.email || "");
      setIsActive(!!res?.data?.isActive);
      // keep roleId as-is
      onResetPasswords();
      message.success("Profile reloaded.");
    } catch (e) {
      setError("Error reloading profile data.");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async () => {
    if (!validate()) return;

    setSaving(true);
    setError("");
    try {
      const payload = {
        firstName: firstName.trim(),
        lastName:  lastName.trim(),
        isActive,       // required by backend
        roleId,         // required by backend (unchanged role)
        ...(oldPassword && { oldPassword, newPassword }),
      };

      const userId = authData?.userId;
      const res = await axiosInstance.put(`/user/User/${userId}`, payload);

      if (res.status === 200) {
        notification.success({
          message: "Profile updated",
          description: "Your profile changes have been saved.",
        });
        onResetPasswords();
      } else {
        throw new Error("Failed to update profile.");
      }
    } catch (e) {
      const msg = e?.response?.data?.message || "Error updating profile. Please try again.";
      setError(msg);
      notification.error({ message: "Update failed", description: msg });
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ paddingInline: 12, paddingBottom: 24 }}>
      <Breadcrumb />

      {/* Header */}
      <Flex align="center" justify="space-between" wrap gap={12} style={{ marginTop: 8 }}>
        <Space align="center" wrap>
          <Title level={3} style={{ margin: 0 }}>Profile Settings</Title>
          <Tooltip title="What is this page?">
            <Button
              type="text"
              icon={<InfoCircleOutlined />}
              onClick={() => setShowInfo(true)}
              aria-label="Profile Settings Info"
            />
          </Tooltip>
        </Space>

        <Space wrap>
          <Button icon={<UndoOutlined />} onClick={onReload}>Reload</Button>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={onSubmit}>
            Save
          </Button>
        </Space>
      </Flex>

      <Divider />

      {error && (
        <Alert
          style={{ marginBottom: 12 }}
          type="error"
          message="Operation Failed"
          description={error}
          showIcon
        />
      )}

      <Card styles={{ body: { paddingTop: 16 } }}>
        {loading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : (
          <>
            {/* Basic Info */}
            <Title level={5} style={{ marginTop: 0 }}>Basic Information</Title>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={12}>
                <Space direction="vertical" style={{ width: "100%" }} size={6}>
                  <Text type="secondary">First Name</Text>
                  <Input
                    size="large"
                    prefix={<UserOutlined />}
                    placeholder="First name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    disabled={saving}
                  />
                </Space>
              </Col>
              <Col xs={24} md={12}>
                <Space direction="vertical" style={{ width: "100%" }} size={6}>
                  <Text type="secondary">Last Name</Text>
                  <Input
                    size="large"
                    prefix={<UserOutlined />}
                    placeholder="Last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={saving}
                  />
                </Space>
              </Col>
              <Col xs={24} md={12}>
                <Space direction="vertical" style={{ width: "100%" }} size={6}>
                  <Text type="secondary">Email</Text>
                  <Input size="large" prefix={<MailOutlined />} value={email} readOnly disabled />
                  <Text type="secondary">Email is managed by the system and cannot be changed here.</Text>
                </Space>
              </Col>
            </Row>

            <Divider />

            {/* Password */}
            <Title level={5} style={{ marginTop: 0 }}>Change Password</Title>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={12}>
                <Space direction="vertical" style={{ width: "100%" }} size={6}>
                  <Text type="secondary">Current Password</Text>
                  <Input.Password
                    size="large"
                    prefix={<LockOutlined />}
                    placeholder="Enter current password to change"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    disabled={saving}
                  />
                </Space>
              </Col>
              <Col xs={24} md={12} />
              <Col xs={24} md={12}>
                <Space direction="vertical" style={{ width: "100%" }} size={6}>
                  <Text type="secondary">New Password</Text>
                  <Input.Password
                    size="large"
                    prefix={<LockOutlined />}
                    placeholder="Leave blank if unchanged"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={saving}
                  />
                </Space>
              </Col>
              <Col xs={24} md={12}>
                <Space direction="vertical" style={{ width: "100%" }} size={6}>
                  <Text type="secondary">Confirm New Password</Text>
                  <Input.Password
                    size="large"
                    prefix={<LockOutlined />}
                    placeholder="Leave blank if unchanged"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    disabled={saving}
                  />
                </Space>
              </Col>
            </Row>

            <Flex justify="space-between" align="center" wrap gap={8} style={{ marginTop: 16 }}>
              <Space wrap>
                <Button onClick={onResetPasswords} icon={<UndoOutlined />} disabled={saving}>
                  Clear Password Fields
                </Button>
              </Space>
              <Space wrap>
                <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={onSubmit}>
                  Save Changes
                </Button>
                <Button icon={<LoginOutlined />} onClick={() => navigate("/login")} disabled={saving}>
                  Logout / Login
                </Button>
              </Space>
            </Flex>
          </>
        )}
      </Card>

      {/* Info Modal */}
      <Modal
        title={
          <Space>
            <InfoCircleOutlined />
            <Text strong>Profile Settings – Guide</Text>
          </Space>
        }
        open={showInfo}
        onCancel={() => setShowInfo(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowInfo(false)}>
            Close
          </Button>,
        ]}
        width={screens.xs ? 520 : 680}
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Text>Update your name details and optionally change your password. Email is read-only on this page.</Text>
          <Divider style={{ margin: "8px 0" }} />
          <Title level={5} style={{ margin: 0 }}>Tips</Title>
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            <li>Click <strong>Reload</strong> to pull the latest profile data from the server.</li>
            <li>To change your password, provide your <strong>current password</strong> and enter your new password twice.</li>
            <li>Passwords must be at least 8 characters.</li>
          </ul>
        </Space>
      </Modal>
    </div>
  );
};

export default ProfileSettingsPage;
